import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { RootState } from "..";

export type ChartType =
  | "bar"
  | "grouped_bar"
  | "donut"
  | "pie"
  | "line"
  | "table"
  | "crosstab";

export interface PendingMemoryEntry {
  dashboardId: string;
  questionId: string;
  datasetId: string;
  questionType: string;
}

export interface ChartDrawerState {
  isDrawerOpen: boolean;
  currentChartType: ChartType | null;
  editingWidgetId: string | null;
  /** Tracks the question written optimistically to session memory on "Create".
   *  Used by discard handlers to undo the write if the user cancels. */
  pendingMemoryEntry: PendingMemoryEntry | null;
  /** Active tab context — the drawer is mounted outside the router, so it reads
   *  the current dashboard/tab from here (e.g. for the tab-time alignment check). */
  dashboardId: string | null;
  tabId: string | null;
}

export const initialState: ChartDrawerState = {
  isDrawerOpen: false,
  currentChartType: null,
  editingWidgetId: null,
  pendingMemoryEntry: null,
  dashboardId: null,
  tabId: null,
};

export const chartDrawerSlice = createSlice({
  name: "chartDrawer",
  initialState,
  reducers: {
    openChartDrawer: (
      state,
      action: PayloadAction<{ chartType: ChartType; widgetId: string }>,
    ) => {
      state.isDrawerOpen = true;
      state.currentChartType = action.payload.chartType;
      state.editingWidgetId = action.payload.widgetId;
    },
    closeChartDrawer: (state) => {
      state.isDrawerOpen = false;
      state.currentChartType = null;
      state.editingWidgetId = null;
      state.pendingMemoryEntry = null;
    },
    setPendingMemoryEntry: (
      state,
      action: PayloadAction<PendingMemoryEntry | null>,
    ) => {
      state.pendingMemoryEntry = action.payload;
    },
    setChartType: (state, action: PayloadAction<ChartType>) => {
      state.currentChartType = action.payload;
    },
    setDrawerTabContext: (
      state,
      action: PayloadAction<{ dashboardId: string | null; tabId: string | null }>,
    ) => {
      state.dashboardId = action.payload.dashboardId;
      state.tabId = action.payload.tabId;
    },
  },
});

export const {
  openChartDrawer,
  closeChartDrawer,
  setChartType,
  setPendingMemoryEntry,
  setDrawerTabContext,
} = chartDrawerSlice.actions;

export const chartDrawerDispatcher = createDispatchers(
  chartDrawerSlice.actions,
);
export const chartDrawerSelector = (state: RootState) => state.chartDrawer;

export const chartDrawerReducer = chartDrawerSlice.reducer;
